import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  FaVideo,
  FaClock,
  FaSearch,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationCircle,
  FaUser,
  FaEnvelope,
  FaStar,
  FaDownload,
  FaPlay,
  FaCalendarAlt,
  FaSyncAlt,
  FaFileAlt,
  FaStop
} from 'react-icons/fa';
import { supabase } from './supabaseClient';
import { API_BASE_URL } from './config/apiConfig';

export default function AdminSessionsPage() {
  const { isArabic } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedSession, setExpandedSession] = useState(null);
  const [contractStatus, setContractStatus] = useState({});

  // Load sessions and stats
  useEffect(() => {
    loadSessions();
    loadStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadSessions();
      loadStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [statusFilter, currentPage, searchQuery]);

  async function loadSessions() {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams({
        status: statusFilter,
        page: currentPage,
        limit: '20',
        ...(searchQuery && { search: searchQuery })
      });

      const response = await fetch(`${API_BASE_URL}/api/admin/sessions?${params}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }

      const result = await response.json();
      setSessions(result.sessions || []);
      setTotalPages(result.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_BASE_URL}/api/admin/sessions/stats`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setStats(result.stats);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }

  // Contract check function
  async function checkContractStatus(bookingId) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(
        `${API_BASE_URL}/api/admin/contracts/check/${bookingId}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setContractStatus(prev => ({
          ...prev,
          [bookingId]: result
        }));
      }
    } catch (err) {
      console.error('Error checking contract:', err);
    }
  }

  // Contract download function
  async function downloadContract(bookingId) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('Not authenticated');
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/admin/contracts/${bookingId}/download`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        alert('Failed to download contract');
        return;
      }

      const result = await response.json();
      if (result.success && result.downloadUrl) {
        // Open contract in new tab
        window.open(result.downloadUrl, '_blank');

        // Refresh contract status to update download count
        await checkContractStatus(bookingId);
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download contract');
    }
  }

  function getStatusBadge(status) {
    const statusConfig = {
      scheduled: {
        label: isArabic ? 'مجدولة' : 'Scheduled',
        bg: '#e3f2fd',
        color: '#2196F3',
        icon: FaCalendarAlt
      },
      should_start: {
        label: isArabic ? 'يجب أن تبدأ' : 'Should Start',
        bg: '#fff3e0',
        color: '#FF9800',
        icon: FaExclamationCircle
      },
      in_progress: {
        label: isArabic ? 'قيد التنفيذ' : 'In Progress',
        bg: '#e8f5e9',
        color: '#4CAF50',
        icon: FaPlay
      },
      completed: {
        label: isArabic ? 'مكتملة' : 'Completed',
        bg: '#f3e5f5',
        color: '#9C27B0',
        icon: FaCheckCircle
      }
    };

    const config = statusConfig[status] || statusConfig.scheduled;
    const Icon = config.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 20,
        background: config.bg,
        color: config.color,
        fontSize: 12,
        fontWeight: 600
      }}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  }

  function formatDateTime(dateStr, timeStr) {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const time = timeStr || '';
    return `${date.toLocaleDateString()} ${time}`;
  }

  function formatRelativeTime(timestamp) {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return isArabic ? 'الآن' : 'Just now';
    if (diffMins < 60) return isArabic ? `منذ ${diffMins} دقيقة` : `${diffMins}m ago`;
    if (diffHours < 24) return isArabic ? `منذ ${diffHours} ساعة` : `${diffHours}h ago`;
    return isArabic ? `منذ ${diffDays} يوم` : `${diffDays}d ago`;
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontSize: 32,
          fontWeight: 800,
          color: '#111827',
          margin: '0 0 8px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <FaVideo size={32} color="#ff7a1a" />
          {isArabic ? 'تتبع الجلسات' : 'Sessions Tracking'}
        </h1>
        <p style={{ fontSize: 16, color: '#6b7280', margin: 0 }}>
          {isArabic 
            ? 'عرض جميع الجلسات النشطة والمكتملة مع حالة التسجيل والبريد الإلكتروني والتقييم'
            : 'View all active and completed sessions with recording, email, and rating status'}
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 32
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: 20,
            borderRadius: 12,
            color: 'white'
          }}>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>
              {isArabic ? 'إجمالي الجلسات' : 'Total Sessions'}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800 }}>
              {stats.totalSessions}
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            padding: 20,
            borderRadius: 12,
            color: 'white'
          }}>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>
              {isArabic ? 'قيد التنفيذ' : 'In Progress'}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800 }}>
              {stats.inProgressSessions}
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            padding: 20,
            borderRadius: 12,
            color: 'white'
          }}>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>
              {isArabic ? 'مكتملة' : 'Completed'}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800 }}>
              {stats.completedSessions}
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            padding: 20,
            borderRadius: 12,
            color: 'white'
          }}>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>
              {isArabic ? 'مع تسجيلات' : 'With Recordings'}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800 }}>
              {stats.sessionsWithRecordings}
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            padding: 20,
            borderRadius: 12,
            color: 'white'
          }}>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>
              {isArabic ? 'تم إرسال البريد' : 'Emails Sent'}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800 }}>
              {stats.sessionsWithEmails}
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            padding: 20,
            borderRadius: 12,
            color: 'white'
          }}>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>
              {isArabic ? 'مع تقييمات' : 'With Ratings'}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800 }}>
              {stats.sessionsWithRatings}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{
        background: '#fff',
        padding: 20,
        borderRadius: 12,
        marginBottom: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ position: 'relative' }}>
            <FaSearch size={18} style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af'
            }} />
            <input
              type="text"
              placeholder={isArabic ? 'بحث...' : 'Search...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && loadSessions()}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '2px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none'
              }}
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            padding: '10px 16px',
            border: '2px solid #e5e7eb',
            borderRadius: 8,
            fontSize: 14,
            cursor: 'pointer',
            outline: 'none'
          }}
        >
          <option value="all">{isArabic ? 'الكل' : 'All Sessions'}</option>
          <option value="in_progress">{isArabic ? 'قيد التنفيذ' : 'In Progress'}</option>
          <option value="completed">{isArabic ? 'مكتملة' : 'Completed'}</option>
        </select>
        <button
          onClick={() => {
            loadSessions();
            loadStats();
          }}
          style={{
            padding: '10px 20px',
            background: '#ff7a1a',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <FaSyncAlt size={16} />
          {isArabic ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      {/* Sessions List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{
            width: 40,
            height: 40,
            border: '3px solid #e5e7eb',
            borderTopColor: '#ff7a1a',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6b7280' }}>
            {isArabic ? 'جاري التحميل...' : 'Loading sessions...'}
          </p>
        </div>
      ) : error ? (
        <div style={{
          background: '#fee2e2',
          border: '2px solid #fca5a5',
          borderRadius: 12,
          padding: 20,
          textAlign: 'center',
          color: '#991b1b'
        }}>
          <FaExclamationCircle size={24} style={{ marginBottom: 8 }} />
          <p>{error}</p>
        </div>
      ) : sessions.length === 0 ? (
        <div style={{
          background: '#f9fafb',
          border: '2px dashed #e5e7eb',
          borderRadius: 12,
          padding: 60,
          textAlign: 'center',
          color: '#6b7280'
        }}>
          <FaVideo size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
          <p style={{ fontSize: 16, fontWeight: 600 }}>
            {isArabic ? 'لا توجد جلسات' : 'No sessions found'}
          </p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sessions.map((session) => (
              <div
                key={session.id}
                style={{
                  background: '#fff',
                  border: '2px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 20,
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#ff7a1a';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 122, 26, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={() => {
                  const newExpanded = expandedSession === session.id ? null : session.id;
                  setExpandedSession(newExpanded);
                  // Check contract status when expanding
                  if (newExpanded) {
                    checkContractStatus(newExpanded);
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 300 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      {getStatusBadge(session.sessionStatus)}
                      <span style={{ fontSize: 14, color: '#6b7280' }}>
                        {session.subject || 'N/A'}
                      </span>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                      {session.teacherName} → {session.studentName}
                    </div>
                    <div style={{ fontSize: 14, color: '#6b7280', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <FaCalendarAlt size={14} /> {formatDateTime(session.date, session.timeFrom)}
                      </span>
                      {session.sessionStartedAt && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <FaPlay size={14} /> {isArabic ? 'بدأت:' : 'Started:'} {formatRelativeTime(session.sessionStartedAt)}
                        </span>
                      )}
                      {session.sessionEndedAt && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <FaStop size={14} /> {isArabic ? 'انتهت:' : 'Ended:'} {formatRelativeTime(session.sessionEndedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {session.recordingUrl ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 20,
                        background: '#e8f5e9',
                        color: '#2e7d32',
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        <FaDownload size={14} />
                        {isArabic ? 'تسجيل متاح' : 'Recording'}
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 20,
                        background: '#f3f4f6',
                        color: '#6b7280',
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        <FaVideo size={14} />
                        {isArabic ? 'لا يوجد تسجيل' : 'No Recording'}
                      </span>
                    )}
                    {session.emailSent ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 20,
                        background: '#e3f2fd',
                        color: '#1976d2',
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        <FaEnvelope size={14} />
                        {isArabic ? 'تم الإرسال' : 'Email Sent'}
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 20,
                        background: '#fff3e0',
                        color: '#f57c00',
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        <FaEnvelope size={14} />
                        {isArabic ? 'لم يتم الإرسال' : 'Not Sent'}
                      </span>
                    )}
                    {session.isRated ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 20,
                        background: '#fff8e1',
                        color: '#f9a825',
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        <FaStar size={14} color="#f9a825" />
                        {isArabic ? 'تم التقييم' : 'Rated'}
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 20,
                        background: '#f3f4f6',
                        color: '#6b7280',
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        <FaStar size={14} />
                        {isArabic ? 'لم يتم التقييم' : 'Not Rated'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedSession === session.id && (
                  <div style={{
                    marginTop: 20,
                    paddingTop: 20,
                    borderTop: '2px solid #e5e7eb',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: 16
                  }}>
                    <div>
                      <h4 style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>
                        {isArabic ? 'تفاصيل الجلسة' : 'Session Details'}
                      </h4>
                      <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.8 }}>
                        <div style={{ marginBottom: 8, padding: '6px 10px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                          <strong style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase' }}>
                            {isArabic ? 'معرف الجلسة:' : 'Session ID:'}
                          </strong>
                          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151', marginTop: 4, wordBreak: 'break-all' }}>
                            {session.id}
                          </div>
                        </div>
                        <div><strong>{isArabic ? 'المعلم:' : 'Teacher:'}</strong> {session.teacherName}</div>
                        <div><strong>{isArabic ? 'الطالب:' : 'Student:'}</strong> {session.studentName}</div>
                        <div><strong>{isArabic ? 'الموضوع:' : 'Subject:'}</strong> {session.subject || 'N/A'}</div>
                        <div><strong>{isArabic ? 'التاريخ:' : 'Date:'}</strong> {formatDateTime(session.date, session.timeFrom)}</div>
                        <div><strong>{isArabic ? 'الوقت:' : 'Time:'}</strong> {session.timeFrom} - {session.timeTo}</div>
                        {session.meetingLink && (
                          <div>
                            <strong>{isArabic ? 'رابط الاجتماع:' : 'Meeting Link:'}</strong>{' '}
                            <a href={session.meetingLink} target="_blank" rel="noopener noreferrer" style={{ color: '#ff7a1a' }}>
                              {isArabic ? 'فتح' : 'Open'}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <h4 style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>
                        {isArabic ? 'حالة الجلسة' : 'Session Status'}
                      </h4>
                      <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.8 }}>
                        {session.sessionStartedAt && (
                          <div><strong>{isArabic ? 'بدأت في:' : 'Started:'}</strong> {new Date(session.sessionStartedAt).toLocaleString()}</div>
                        )}
                        {session.sessionEndedAt && (
                          <div><strong>{isArabic ? 'انتهت في:' : 'Ended:'}</strong> {new Date(session.sessionEndedAt).toLocaleString()}</div>
                        )}
                        {session.sessionStartedAt && session.sessionEndedAt && (
                          <div>
                            <strong>{isArabic ? 'المدة:' : 'Duration:'}</strong>{' '}
                            {Math.round((new Date(session.sessionEndedAt) - new Date(session.sessionStartedAt)) / 60000)} {isArabic ? 'دقيقة' : 'minutes'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <h4 style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>
                        {isArabic ? 'التسجيل والبريد' : 'Recording & Email'}
                      </h4>
                      <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.8 }}>
                        {session.recordingUrl ? (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ marginBottom: 8 }}>
                              <strong>{isArabic ? 'رابط التسجيل:' : 'Recording Link:'}</strong>
                            </div>
                            <div style={{ 
                              padding: '8px 12px', 
                              background: '#f9fafb', 
                              borderRadius: 6, 
                              border: '1px solid #e5e7eb',
                              marginBottom: 8,
                              wordBreak: 'break-all',
                              fontSize: 13,
                              fontFamily: 'monospace'
                            }}>
                              <a href={session.recordingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#ff7a1a', textDecoration: 'none' }}>
                                {session.recordingUrl}
                              </a>
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <strong>{isArabic ? 'كلمة المرور:' : 'Password:'}</strong>{' '}
                              <span style={{ color: '#6b7280' }}>
                                {isArabic ? 'غير مطلوب (رابط آمن)' : 'Not required (secure link)'}
                              </span>
                            </div>
                            {session.recordingAvailableAt && (
                              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                {isArabic ? 'متاح منذ:' : 'Available since:'} {formatRelativeTime(session.recordingAvailableAt)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div><strong>{isArabic ? 'التسجيل:' : 'Recording:'}</strong> {isArabic ? 'غير متاح' : 'Not available'}</div>
                        )}
                        {session.emailSent ? (
                          <div>
                            <strong>{isArabic ? 'البريد الإلكتروني:' : 'Email:'}</strong> {isArabic ? 'تم الإرسال' : 'Sent'}{' '}
                            {session.postSessionEmailSentAt && (
                              <span style={{ fontSize: 12, color: '#6b7280' }}>
                                ({formatRelativeTime(session.postSessionEmailSentAt)})
                              </span>
                            )}
                          </div>
                        ) : (
                          <div><strong>{isArabic ? 'البريد الإلكتروني:' : 'Email:'}</strong> {isArabic ? 'لم يتم الإرسال' : 'Not sent'}</div>
                        )}
                        {session.isRated ? (
                          <div>
                            <strong>{isArabic ? 'التقييم:' : 'Rating:'}</strong> {isArabic ? 'تم التقييم' : 'Rated'}{' '}
                            {session.ratedAt && (
                              <span style={{ fontSize: 12, color: '#6b7280' }}>
                                ({formatRelativeTime(session.ratedAt)})
                              </span>
                            )}
                          </div>
                        ) : (
                          <div><strong>{isArabic ? 'التقييم:' : 'Rating:'}</strong> {isArabic ? 'لم يتم التقييم' : 'Not rated'}</div>
                        )}
                      </div>
                    </div>

                    {/* Contract Section */}
                    <div>
                      <h4 style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#6b7280',
                        marginBottom: 8,
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}>
                        <FaFileAlt size={14} />
                        {isArabic ? 'العقد القانوني' : 'Legal Contract'}
                      </h4>
                      <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.8 }}>
                        {contractStatus[session.id]?.exists ? (
                          <div>
                            <div style={{ marginBottom: 8 }}>
                              <strong>{isArabic ? 'الحالة:' : 'Status:'}</strong>{' '}
                              <span style={{ color: '#10b981' }}>
                                {isArabic ? 'تم الإنشاء' : 'Generated'} ✓
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                              {isArabic ? 'تم الإنشاء:' : 'Generated:'} {new Date(contractStatus[session.id].contract.generated_at).toLocaleString()}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                              {isArabic ? 'عدد التنزيلات:' : 'Downloads:'} {contractStatus[session.id].contract.download_count || 0}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                              {isArabic ? 'الحجم:' : 'Size:'} {(contractStatus[session.id].contract.file_size_bytes / 1024).toFixed(2)} KB
                            </div>
                            <button
                              onClick={() => downloadContract(session.id)}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#8b5cf6',
                                color: 'white',
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
                            >
                              <FaDownload size={14} />
                              {isArabic ? 'تحميل PDF' : 'Download PDF'}
                            </button>
                          </div>
                        ) : session.payment_status === 'paid' ? (
                          <div style={{ padding: 12, backgroundColor: '#fef3c7', borderRadius: 6, fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <FaExclamationCircle size={16} /> {isArabic ? 'لم يتم إنشاء العقد بعد (قد يستغرق بضع لحظات بعد الدفع)' : 'Contract not yet generated (may take a few moments after payment)'}
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: '#6b7280' }}>
                            {isArabic ? 'سيتم إنشاء العقد بعد اكتمال الدفع' : 'Contract will be generated after payment completion'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              marginTop: 24
            }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  background: currentPage === 1 ? '#f3f4f6' : '#fff',
                  color: currentPage === 1 ? '#9ca3af' : '#374151',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                {isArabic ? 'السابق' : 'Previous'}
              </button>
              <span style={{ fontSize: 14, color: '#6b7280' }}>
                {isArabic ? 'صفحة' : 'Page'} {currentPage} {isArabic ? 'من' : 'of'} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  background: currentPage === totalPages ? '#f3f4f6' : '#fff',
                  color: currentPage === totalPages ? '#9ca3af' : '#374151',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                {isArabic ? 'التالي' : 'Next'}
              </button>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

